import React, { useEffect, useState } from "react";
import axios from "axios";
import { LEGAL } from "../legal/legalConfig";
import { CompanyPolicyLinks } from "../legal/LegalFooterLinks";
import { watchSubscriptionStatus } from "../lib/teamCheckout";

/*
 * Public pricing page (no login needed). Started in manager's PR #13; prices now come from the backend
 * (GET /api/pricing), the same catalog the 5x AI-cost guard checks at the INR price.
 * INR prices are inclusive of GST. Plans marked "Coming soon" are priced but not yet in checkout.
 * Starter and Pro (Oct 2026): monthly INR subscriptions, Gemini 2.5 Flash only, each run within a fixed AI budget.
 */
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inr = (n) => `₹${Number(n).toLocaleString("en-IN")}`;
const usd = (n) => `$${Number(n).toFixed(2)}`;

export function planCards(data) {
  if (!data) return [];
  const free = data.free;
  const cards = [{
    id: "free", name: "Free", price: "₹0", period: "/ 30 days", alt: "Free everywhere",
    features: [`${free.runs_per_30_days} agent runs per 30 days`, "Pick your model (Claude Opus in Complex mode counts as 3 runs)",
      "Fast and Complex modes", "Auditor on every run", "Skills and team workspaces"],
  }];
  for (const p of data.plans) {
    const id = p.id === "starter_pass" ? "starter" : p.id === "pro_pass" ? "pro" : p.id;
    const runs = `${Number(p.runs_per_30_days).toLocaleString("en-IN")} agent runs per 30 days`;
    if (p.id === "team" && p.available) {
      const r = p.renewal || {};
      cards.push({
        id, name: p.name, price: inr(p.inr), period: "/ month per person", highlight: true,
        alt: `${data.price_note} · renews monthly until you cancel`,
        features: [
          runs,
          `Runs use ${p.run_model?.name || p.run_model?.id}; every run counts as 1`,
          `Pay by ${(r.methods || ["UPI Autopay", "card"]).join(" or ")} through Razorpay`,
          `Reminder email before each renewal; cancel any time in Settings`,
          `Outside India: ${usd(p.usd_pass?.usd ?? p.usd)} card payment (Stripe) for a 30-day pass that doesn't renew`,
          "Everything in Free",
        ],
      });
    } else if (p.available && p.checkout === "razorpay_subscription_inr") {
      const r = p.renewal || {};
      cards.push({
        id, name: p.name, price: inr(p.inr), period: "/ month per person",
        alt: `${data.price_note} · renews monthly until you cancel`,
        features: [
          runs,
          `Runs use ${p.run_model?.name || p.run_model?.id}; every run counts as 1`,
          "Each run has a fixed AI budget: a run too large for it is refused before it starts and doesn't count",
          `Pay by ${(r.methods || ["UPI Autopay", "card"]).join(" or ")} through Razorpay`,
          `Reminder email before each renewal; cancel any time in Settings`,
          ...(p.coupons === false ? ["No coupons"] : []),
        ],
      });
    } else {
      cards.push({ id, name: p.name, price: inr(p.inr), period: "/ 30 days per person",
        alt: `${data.price_note} · ${usd(p.usd)} card price`, soon: !p.available, features: [runs] });
    }
  }
  return cards;
}

const T = {"page": "min-h-screen bg-background text-foreground", "muted": "text-slate-500", "card": "rounded-2xl border border-slate-200 bg-white p-6 flex flex-col shadow-sm", "hl": "rounded-2xl border-2 border-emerald-600 bg-white p-6 flex flex-col shadow-md", "link": "underline hover:opacity-80", "badge": "bg-emerald-600 text-white", "soon": "border border-slate-300 text-slate-600", "table": "border-slate-200"};

export default function PricingPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  useEffect(() => {
    document.title = "Pricing · Nimbus";
    axios.get(`${API}/pricing`).then((r) => setData(r.data)).catch(() => setError(true));
    return watchSubscriptionStatus();
  }, []);
  const cards = planCards(data);
  return (
    <div className={T.page} data-testid="public-pricing-page">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-10">
        <nav className="flex items-center justify-between mb-10">
          <a href="/" className="font-bold text-lg" data-testid="pricing-home-link">Nimbus</a>
          <a href="/login" className={T.link} data-testid="pricing-signin-link">Sign in</a>
        </nav>
        <header className="mb-10 text-center">
          <h1 className="text-3xl md:text-4xl font-bold mb-3">Pricing</h1>
          <p className={T.muted}>A data agent that runs real tools on your files and audits every figure. Start free.</p>
          <p className="mt-2 text-sm font-medium" data-testid="pricing-gst-note">All prices in Indian rupees are {LEGAL.priceNote}.</p>
        </header>
        {error && <p className="text-center text-sm text-red-600">Prices couldn't be loaded. Please refresh.</p>}
        <section className="grid gap-6 md:grid-cols-2 lg:grid-cols-4" data-testid="pricing-plans">
          {cards.map((p) => (
            <article key={p.id} className={p.highlight ? T.hl : T.card} data-testid={`pricing-plan-${p.id}`}>
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <h2 className="text-xl font-semibold">{p.name}</h2>
                {p.highlight && <span className={`text-xs px-2 py-0.5 rounded-full ${T.badge}`}>Popular</span>}
                {p.soon && <span className={`text-xs px-2 py-0.5 rounded-full ${T.soon}`}>Coming soon</span>}
              </div>
              <p className="text-3xl font-bold">{p.price}<span className={`text-base font-normal ${T.muted}`}> {p.period}</span></p>
              {p.alt && <p className={`text-sm mt-1 ${T.muted}`}>{p.alt}</p>}
              <ul className="mt-4 space-y-2 text-sm flex-1">
                {p.features.map((f) => <li key={f}>✓ {f}</li>)}
              </ul>
            </article>
          ))}
        </section>
        <section className="mt-12" data-testid="pricing-extras">
          <h2 className="text-xl font-semibold mb-4">Good to know</h2>
          <table className="w-full text-sm">
            <tbody>
              <tr className={`border-b ${T.table}`}><td className="py-2 pr-4">Claude Opus in Complex mode (Free)</td><td className="py-2 text-right font-medium">Counts as 3 runs</td></tr>
              <tr className={`border-b ${T.table}`}><td className="py-2 pr-4">Team renewal payment fails</td><td className="py-2 text-right font-medium">Team continues for {data?.plans?.find((x) => x.id === "team")?.renewal?.grace_days ?? 3} days, then Free</td></tr>
              <tr className={`border-b ${T.table}`}><td className="py-2 pr-4">Enterprise: custom run limits and onboarding</td><td className="py-2 text-right font-medium">Contact {LEGAL.contactEmail}</td></tr>
            </tbody>
          </table>
        </section>
        <section className={`mt-10 text-sm space-y-1 ${T.muted}`}>
          <p data-testid="pricing-checkout-note">{data?.checkout_note}</p>
          <p>See the <a className={T.link} href="/refund">Refund Policy</a> and <a className={T.link} href="/terms">Terms</a> for refunds and cancelling.</p>
        </section>
        <footer className={`mt-12 pt-6 border-t ${T.table} text-xs ${T.muted} space-y-2`} data-testid="pricing-footer">
          <nav className="flex flex-wrap gap-4" aria-label="Legal">
            <a className={T.link} href="/terms" data-testid="pricing-terms-link">Terms</a>
            <a className={T.link} href="/privacy" data-testid="pricing-privacy-link">Privacy</a>
            <a className={T.link} href="/cookies" data-testid="pricing-cookies-link">Cookie Policy</a>
            <a className={T.link} href="/refund" data-testid="pricing-refund-link">Refunds</a>
            <a className={T.link} href="https://razorpay.me/@headkeysoftwaretechnologiespr" target="_blank" rel="noopener noreferrer" data-testid="pricing-pay-us-link">Pay us</a>
            <a className={T.link} href="/help" data-testid="pricing-help-link">Help</a>
          </nav>
          <p>© {new Date().getFullYear()} Nimbus · {LEGAL.companyName} · CIN {LEGAL.companyCin} · #3, 3rd Floor, RR Block, 4th A Main, Santrupthi Nagar, J P Nagar 7th Phase, Bangalore 560078 · {LEGAL.contactEmail}</p>
          <p>Grievance Officer: {LEGAL.grievanceOfficer}, {LEGAL.contactEmail}</p>
          <CompanyPolicyLinks className="flex flex-wrap gap-x-4 gap-y-1" linkClassName={T.link} />
        </footer>
      </div>
    </div>
  );
}
